import type {
  ClientUpdate,
  ClientUserCreate,
  ClientUserUpdate,
  AgentCreate,
  AgentUpdate,
  FormOptionUpdate,
  FormOption,
  SerializedBusiness,
  SerializedBusinessUser,
  SerializedAgent,
  SerializedIntegration,
  IntegrationCreate,
  IntegrationUpdate,
  TwilioPhoneNumberLookupResult,
} from "../types";
import { signOut } from "firebase/auth";
import { auth } from "./firebaseClient";
import { requestReauth } from "./reauth";

/**
 * Force the user out of a session the API has rejected, rather than letting
 * individual call sites silently swallow a 401/403 into a misleading "empty"
 * UI state. A full navigation (not the SPA router) is used deliberately -
 * this is a hard security boundary, and a full reload guarantees AuthProvider
 * re-runs its onAuthStateChanged/getIdTokenResult flow from a clean slate
 * rather than relying on in-memory state that may itself be stale.
 */
/**
 * A 403 that means "prove it's still you", not "you may not do this".
 *
 * The backend returns code `reauth_required` for high-blast-radius actions
 * (super-admin grant/revoke, deleting a business or agent, editing a
 * systemPrompt) when the token's `auth_time` is older than the step-up window.
 * Thrown as its own type so a call site can offer a password prompt and retry,
 * rather than showing a dead-end permission error or kicking the user out.
 */
export class ReauthRequiredError extends Error {
  readonly code = "reauth_required";

  constructor(message: string) {
    super(message);
    this.name = "ReauthRequiredError";
  }
}

function redirectTo(path: "/login" | "/unauthorized") {
  if (window.location.pathname === path) {
    return;
  }
  if (path === "/login") {
    // login.tsx's beforeLoad reads this `redirect` search param and sends
    // the user back here after signing in - preserve it on the hard
    // redirect too, or a session-expired user loses their place.
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
    return;
  }
  window.location.href = path;
}

// Normalize API URL - ensure no trailing slash
const normalizeApiUrl = (url: string): string => {
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

// Use relative URL in production (Firebase Hosting will proxy to Cloud Run)
// Use full URL in development (direct connection to local server)
const API_BASE_URL = import.meta.env.PROD
  ? "/api" // Relative URL - Firebase Hosting will proxy to Cloud Run
  : normalizeApiUrl(
      import.meta.env.VITE_SUPER_ADMIN_API_URL || "http://localhost:3004/api"
    );

/**
 * Get Firebase ID token for authenticated user
 * Force refresh to ensure custom claims are included
 */
async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  try {
    // Force refresh to get latest custom claims
    const token = await user.getIdToken(true);

    return token;
  } catch (error) {
    console.error("[API] Error getting ID token:", error);
    return null;
  }
}

async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit,
  /**
   * Set on the single automatic retry after a successful re-auth, so a server
   * that keeps answering `reauth_required` cannot put the user in a prompt loop.
   */
  isReauthRetry = false
): Promise<T> {
  // Get Firebase ID token for authentication
  const idToken = await getIdToken();

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };

  // Only set Content-Type if there's a body (not for DELETE requests without body)
  if (options?.body) {
    headers["Content-Type"] = "application/json";
  }

  // Add Authorization header if we have a token
  if (idToken) {
    headers["Authorization"] = `Bearer ${idToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Try to get error message from response - handle both JSON and text
    let error: { error?: string; message?: string; code?: string } = {
      error: "Unknown error",
    };
    const contentType = response.headers.get("content-type");

    try {
      if (contentType?.includes("application/json")) {
        error = await response.json();
      } else {
        // If not JSON, get text
        const text = await response.text();
        error = {
          error: text || `HTTP ${response.status} ${response.statusText}`,
        };
      }
    } catch {
      // ignore; we'll fall back to HTTP status
      error = { error: `HTTP ${response.status} ${response.statusText}` };
    }

    // Enhanced error logging for 401s and 403s
    if (response.status === 401) {
      console.error("[API] ❌ 401 Unauthorized - Authentication failed:", {
        endpoint,
        fullUrl: `${API_BASE_URL}${endpoint}`,
        error: error.error || error.message,
        status: response.status,
        statusText: response.statusText,
        contentType,
        user: auth.currentUser?.email,
        hasToken: !!idToken,
        responseHeaders: Object.fromEntries(response.headers.entries()),
        responseBody: error,
      });
      // No valid session (missing/expired/invalid token) - force a clean
      // sign-out and send them back to log in, instead of leaving the
      // dashboard shell rendered while every API call silently 401s.
      // Redirect regardless of whether signOut itself succeeds (.finally),
      // but .catch first so a rejected signOut (network blip, etc.) doesn't
      // surface as an unhandled promise rejection.
      signOut(auth)
        .catch((err) => {
          console.error("[API] signOut failed:", err);
        })
        .finally(() => redirectTo("/login"));
    } else if (response.status === 403) {
      console.error("[API] ❌ 403 Forbidden - Authorization failed:", {
        endpoint,
        error: error.error || error.message,
        status: response.status,
        user: auth.currentUser?.email,
      });
      // Distinguish the two shapes of 403 the backend sends (see
      // apps/super-admin-api/src/auth.ts): verifyAuthToken's "Unauthorized"
      // means this user has no valid role at all - redirect them out
      // entirely. requireWriteAccess/requireSuperAdminRole's "Forbidden"
      // means a legitimate "support" user hit an action that specifically
      // needs super-admin - that's normal, expected, per-action feedback a
      // support user should see inline (e.g. a toast), not a full kick-out.
      // A third shape: the action needs a fresh sign-in, not a different role.
      // Signing the user out here would be exactly wrong - they hold the right
      // role, they just need to re-confirm identity - so this neither redirects
      // nor kicks out, it hands the call site a typed error to prompt on.
      if (error.code === "reauth_required") {
        // Prompt, then replay the request once. Deliberately transparent: the
        // caller gets its normal result and never learns a re-auth happened.
        if (!isReauthRetry && (await requestReauth())) {
          return apiRequest<T>(endpoint, options, true);
        }
        throw new ReauthRequiredError(
          error.message || "This action needs you to confirm your identity again."
        );
      }

      if (error.error === "Unauthorized") {
        redirectTo("/unauthorized");
      }
    }

    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// Clients
export async function fetchAllClients() {
  return apiRequest<{ clients: SerializedBusiness[] }>("/clients");
}

export interface DashboardStats {
  totalClients: number;
  totalActiveAgents: number;
  agentsByType: { typeId: string; count: number }[];
  activeIntegrations: number;
}

export async function getDashboardStats() {
  return apiRequest<DashboardStats>("/dashboard/stats");
}

export async function fetchClient(id: string) {
  return apiRequest<{
    client: SerializedBusiness;
    users: SerializedBusinessUser[];
    agents: SerializedAgent[];
    integrations: SerializedIntegration[];
  }>(`/clients/${id}`);
}

export async function createClient(data: ClientUpdate) {
  return apiRequest<{ success: boolean; id?: string; error?: string }>(
    "/clients",
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

export async function updateClient(id: string, data: ClientUpdate) {
  return apiRequest<{ success: boolean; error?: string }>(`/clients/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteClient(id: string) {
  return apiRequest<{ success: boolean; error?: string }>(`/clients/${id}`, {
    method: "DELETE",
  });
}

// Client Users
export async function createClientUser(
  businessId: string,
  data: ClientUserCreate
) {
  return apiRequest<{ success: boolean; id?: string; error?: string }>(
    `/clients/${businessId}/users`,
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

export async function updateClientUser(
  businessId: string,
  userId: string,
  data: ClientUserUpdate
) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/clients/${businessId}/users/${userId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

export async function deleteClientUser(businessId: string, userId: string) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/clients/${businessId}/users/${userId}`,
    {
      method: "DELETE",
    }
  );
}

// Agents
export async function createAgent(businessId: string, data: AgentCreate) {
  return apiRequest<{ success: boolean; id?: string; error?: string }>(
    `/clients/${businessId}/agents`,
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

export async function updateAgent(agentId: string, data: AgentUpdate) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/agents/${agentId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

/**
 * Config history for an agent (DRA-345), newest first. `previous` holds the
 * values the agent had *before* that edit, for exactly the fields it changed.
 */
export interface AgentRevision {
  id: string;
  previous: Record<string, unknown>;
  changedKeys: string[];
  actorUid: string | null;
  actorEmail: string | null;
  createdAt: string;
  revertedFromRevisionId?: string | null;
}

export async function fetchAgentRevisions(agentId: string) {
  return apiRequest<{ success: boolean; revisions?: AgentRevision[]; error?: string }>(
    `/agents/${agentId}/revisions`
  );
}

export async function revertAgentRevision(agentId: string, revisionId: string) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/agents/${agentId}/revisions/${revisionId}/revert`,
    { method: "POST" }
  );
}

export async function deleteAgent(agentId: string) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/agents/${agentId}`,
    {
      method: "DELETE",
    }
  );
}

export async function lookupTwilioPhoneNumber(phoneNumber: string, businessId?: string) {
  const params = new URLSearchParams({ phoneNumber });
  // Verify against the client's own Twilio account when it's a BYO business,
  // otherwise the platform default (DRA-306). Without this, the interactive
  // verify always hit the platform account and rejected valid BYO numbers.
  if (businessId) params.set("businessId", businessId);
  return apiRequest<TwilioPhoneNumberLookupResult>(`/twilio/phone-numbers/lookup?${params.toString()}`);
}

// Form Options
export async function fetchFormOptions(type: string) {
  return apiRequest<{ options: FormOption[] }>(`/form-options/${type}`);
}

export async function createFormOption(
  type: string,
  value: string,
  label: string,
  order?: number,
  additionalData?: {
    description?: string;
    metadata?: string;
    model?: string;
    configuration?: string;
  }
) {
  return apiRequest<{ success: boolean; id?: string; error?: string }>(
    `/form-options/${type}`,
    {
      method: "POST",
      body: JSON.stringify({
        value,
        label,
        order,
        additionalData,
      }),
    }
  );
}

export async function updateFormOption(
  type: string,
  id: string,
  data: FormOptionUpdate
) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/form-options/${type}/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

export async function deleteFormOption(type: string, id: string) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/form-options/${type}/${id}`,
    {
      method: "DELETE",
    }
  );
}

// Wrapper functions that match action signatures for easy migration
export async function getFormOptions(type: string) {
  const response = await fetchFormOptions(type);
  return response.options;
}

// Alias for createClientAndAgent to match action signature
export const createClientAndAgent = createClient;

// Outbound Calls
export async function triggerOutboundCall(
  agentId: string,
  phoneNumber: string,
  businessId?: string,
  webhookDomainOverride?: string
) {
  return apiRequest<{ success: boolean; callSid?: string; error?: string }>(
    "/outbound-call",
    {
      method: "POST",
      body: JSON.stringify({ agentId, phoneNumber, businessId, webhookDomainOverride }),
    }
  );
}

// Transcripts
export interface TranscriptConversation {
  id: string;
  conversationId: string;
  sessionId?: string;
  channel?: "chat" | "voice" | "sms";
  status?: "connected" | "active" | "closed";
  agentId?: string;
  businessId: string;
  sessionDomain?: string;
  publicToken?: string;
  callSid?: string;
  fromNumber?: string;
  toNumber?: string;
  /** The person on the call: the caller inbound, the number we dialed outbound. */
  customerNumber?: string;
  /** Name captured while booking or requesting a callback; null when none. */
  callerName?: string | null;
  /** Set when the caller asked to be called back - the number to ring. */
  callbackPhone?: string | null;
  createdAt?: string;
  updatedAt?: string;
  lastMessageAt?: string;
  endedAt?: string;
  turns?: number;
  durationMs?: number;
  idleMs?: number;
  [key: string]: unknown;
}

export interface TranscriptMessage {
  id: string;
  conversationId: string;
  channel?: "chat" | "voice" | "sms";
  role: "system" | "user" | "assistant";
  roleOrder?: number;
  turn?: number | null;
  sortKey?: number | null;
  content: string;
  contentLength?: number;
  contentSha256?: string;
  model?: string;
  finishReason?: string | null;
  openaiDurationMs?: number;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  } | null;
  createdAt?: string;
  [key: string]: unknown;
}

export interface TranscriptsListResponse {
  conversations: TranscriptConversation[];
  filters: {
    businessId: string;
    channel?: "chat" | "voice" | "sms";
    agentId?: string;
    from?: string;
    to?: string;
    timeField: string;
    order: "asc" | "desc";
    limit: number;
  };
}

export interface TranscriptSessionResponse {
  conversation: TranscriptConversation;
  messages?: TranscriptMessage[];
}

export interface GetTranscriptsParams {
  businessId: string;
  sessionId?: string;
  channel?: "chat" | "voice" | "sms";
  agentId?: string;
  from?: string | number; // ISO string or epoch millis
  to?: string | number; // ISO string or epoch millis
  timeField?: "createdAt" | "updatedAt" | "lastMessageAt" | "endedAt";
  order?: "asc" | "desc";
  limit?: number;
  minTurns?: number;
  includeMessages?: boolean;
  messageLimit?: number;
}

/**
 * Get transcript conversations and/or messages.
 *
 * If sessionId is provided, returns a single conversation with optional messages.
 * Otherwise, returns a list of conversations matching the filters.
 */
export async function getTranscripts(
  params: GetTranscriptsParams
): Promise<TranscriptsListResponse | TranscriptSessionResponse> {
  const queryParams = new URLSearchParams();
  queryParams.set("businessId", params.businessId);
  if (params.sessionId) queryParams.set("sessionId", params.sessionId);
  if (params.channel) queryParams.set("channel", params.channel);
  if (params.agentId) queryParams.set("agentId", params.agentId);
  if (params.from !== undefined) {
    queryParams.set(
      "from",
      typeof params.from === "number" ? String(params.from) : params.from
    );
  }
  if (params.to !== undefined) {
    queryParams.set(
      "to",
      typeof params.to === "number" ? String(params.to) : params.to
    );
  }
  if (params.timeField) queryParams.set("timeField", params.timeField);
  if (params.order) queryParams.set("order", params.order);
  if (params.limit !== undefined)
    queryParams.set("limit", String(params.limit));
  if (params.minTurns !== undefined)
    queryParams.set("minTurns", String(params.minTurns));
  if (params.includeMessages !== undefined)
    queryParams.set("includeMessages", String(params.includeMessages));
  if (params.messageLimit !== undefined)
    queryParams.set("messageLimit", String(params.messageLimit));

  return apiRequest<TranscriptsListResponse | TranscriptSessionResponse>(
    `/transcripts?${queryParams.toString()}`
  );
}

// Integrations
export async function createIntegration(
  businessId: string,
  data: IntegrationCreate
) {
  return apiRequest<{ success: boolean; id?: string; error?: string }>(
    `/clients/${businessId}/integrations`,
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

export async function updateIntegration(
  integrationId: string,
  data: IntegrationUpdate
) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/integrations/${integrationId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

export async function deleteIntegration(integrationId: string) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/integrations/${integrationId}`,
    {
      method: "DELETE",
    }
  );
}

// Campaigns
export async function createCampaign(
  integrationId: string,
  data: import("../types").CampaignCreate
) {
  return apiRequest<{ success: boolean; id?: string; error?: string }>(
    `/integrations/${integrationId}/campaigns`,
    {
      method: "POST",
      body: JSON.stringify(data),
    }
  );
}

export async function updateCampaign(
  campaignId: string,
  data: import("../types").CampaignUpdate
) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/campaigns/${campaignId}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    }
  );
}

export async function deleteCampaign(campaignId: string) {
  return apiRequest<{ success: boolean; error?: string }>(
    `/campaigns/${campaignId}`,
    {
      method: "DELETE",
    }
  );
}

export async function launchCampaign(campaignId: string) {
  return apiRequest<{ success: boolean; error?: string; contactsAdded?: number }>(
    `/campaigns/${campaignId}/launch`,
    {
      method: "POST",
    }
  );
}

export async function getCampaigns(integrationId: string) {
  return apiRequest<{ campaigns: import("../types").Campaign[] }>(
    `/integrations/${integrationId}/campaigns`
  );
}

// Audit Logs
export interface AuditLogEntry {
  id: string;
  securitySensitive: boolean;
  action: string | null;
  actorUid: string | null;
  actorEmail: string | null;
  targetUserId: string | null;
  outcome: "success" | "denied" | "error" | null;
  detail: string | null;
  method: string | null;
  url: string | null;
  routeParams: unknown | null;
  bodySummary: string | null;
  statusCode: number | null;
  ip: string | null;
  createdAt: string | null;
}

export interface AuditLogsResponse {
  logs: AuditLogEntry[];
  nextCursor: string | null;
  filters: {
    actorUid?: string;
    url?: string;
    from?: string;
    to?: string;
    limit: number;
  };
}

export interface GetAuditLogsParams {
  actorUid?: string;
  url?: string;
  from?: string | number; // ISO string or epoch millis
  to?: string | number; // ISO string or epoch millis
  limit?: number;
  cursor?: string;
}

/**
 * Get audit log entries (DRA-251) - reads the `auditLogs` collection written
 * by the general onResponse hook and the super-admin escalation routes' own
 * security-tagged entries. See apps/super-admin-api/src/auditLog.ts.
 */
export async function getAuditLogs(
  params: GetAuditLogsParams = {}
): Promise<AuditLogsResponse> {
  const queryParams = new URLSearchParams();
  if (params.actorUid) queryParams.set("actorUid", params.actorUid);
  if (params.url) queryParams.set("url", params.url);
  if (params.from !== undefined) {
    queryParams.set(
      "from",
      typeof params.from === "number" ? String(params.from) : params.from
    );
  }
  if (params.to !== undefined) {
    queryParams.set(
      "to",
      typeof params.to === "number" ? String(params.to) : params.to
    );
  }
  if (params.limit !== undefined)
    queryParams.set("limit", String(params.limit));
  if (params.cursor) queryParams.set("cursor", params.cursor);

  return apiRequest<AuditLogsResponse>(`/audit-logs?${queryParams.toString()}`);
}

// Usage & Cost Rollups (DRA-236)
export interface UsageRollupUnpriced {
  messageCount: number;
  models: string[];
}

export interface UsageRollupEntry {
  date: string;
  businessId: string;
  messageCount: number;
  conversationCount: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  llmCostUsd: number;
  voiceMinutes: number;
  voiceCostUsd: number;
  totalCostUsd: number;
  unpriced: UsageRollupUnpriced;
  computedAt?: string;
}

export interface UsageRollupsSummary {
  messageCount: number;
  conversationCount: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  llmCostUsd: number;
  voiceMinutes: number;
  voiceCostUsd: number;
  totalCostUsd: number;
  unpriced: UsageRollupUnpriced;
}

export interface UsageRollupsResponse {
  rollups: UsageRollupEntry[];
  summary: UsageRollupsSummary;
  filters: {
    businessId: string;
    from: string;
    to: string;
  };
}

export interface GetUsageRollupsParams {
  businessId: string;
  from?: string; // "YYYY-MM-DD"
  to?: string; // "YYYY-MM-DD"
}

/**
 * Get a business's daily usage/cost rollups (DRA-235's nightly rollup) plus
 * a pre-aggregated summary over a date range. Defaults to the last 30 days
 * server-side when from/to are omitted - see apps/super-admin-api's
 * GET /api/usage-rollups.
 */
export async function getUsageRollups(
  params: GetUsageRollupsParams
): Promise<UsageRollupsResponse> {
  const queryParams = new URLSearchParams();
  queryParams.set("businessId", params.businessId);
  if (params.from) queryParams.set("from", params.from);
  if (params.to) queryParams.set("to", params.to);

  return apiRequest<UsageRollupsResponse>(
    `/usage-rollups?${queryParams.toString()}`
  );
}
